import { Component, Inject } from "@angular/core";
import { NonParticipantUserTypes } from "@common/ADAPT.Common.Model/embed/user-type";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { UserService } from "@common/user/user.service";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogWithDiscardConfirmationComponent } from "@common/ux/adapt-common-dialog/base-dialog-with-discard-confirmation.component/base-dialog-with-discard-confirmation.component";
import { Breakpoint } from "@common/ux/responsive/breakpoint";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { EMPTY, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

@Component({
    templateUrl: "./add-team-dialog.component.html",
    styleUrls: ["./add-team-dialog.component.scss"],
    standalone: false,
})
export class AddTeamDialogComponent extends BaseDialogWithDiscardConfirmationComponent<Team> {
    public readonly dialogName = "AddTeam";
    public entitiesToConfirm: IBreezeEntity[] = [];

    public teamLeaderLabel = this.teamsService.teamLeaderLabel;
    public readonly allowedUserTypes = NonParticipantUserTypes;
    public isTwoColumnLayout = false;
    public canCreateOwnerTeam = false;
    public canCreateLeadersTeam = false;

    public isOwnerTeam = false;
    public isLeaderTeam = false;
    public teamLocations: TeamLocation[] = [];

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public team: Team,
        protected commonDataService: CommonDataService,
        private teamsService: CommonTeamsService,
        private userService: UserService,
        private authService: AuthorisationService,
        responsiveService: ResponsiveService,
    ) {
        super();

        this.autoResolveData = this.team;
        this.entitiesToConfirm = [this.team];

        responsiveService.currentBreakpoint$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((breakpoint) => this.isTwoColumnLayout = breakpoint.is(Breakpoint.LG));

        teamsService.promiseToGetActiveOwnerTeams().then((ownerTeams) => {
            this.canCreateOwnerTeam = ownerTeams.length === 0;
        });
        teamsService.promiseToGetLeadershipTeam().then((leadersTeam) => {
            this.canCreateLeadersTeam = !leadersTeam;
        });
    }

    public get entitiesAreUnmodifiedOrInvalid() {
        return this.team.name === ""
            || this.team.teamLeaderPerson === null
            || super.entitiesAreUnmodifiedOrInvalid;
    }

    public get hasUnsavedEntity() {
        return !this.entitiesAreUnmodifiedOrInvalid;
    }

    public get isPrivateNonStakeholderManagerOrCoach() {
        const currentPerson = this.userService.currentPerson;
        return this.team.isPrivate && !(this.authService.isStakeholderManager() && !currentPerson?.getActiveConnections().length)
            && !currentPerson?.isCoach();
    }

    public addEntitiesToConfirm(entities: IBreezeEntity[]) {
        for (const entity of entities) {
            if (!this.entitiesToConfirm.includes(entity)) {
                this.entitiesToConfirm.push(entity);
            }
        }
    }

    public onTeamLocationEntityAdded(entity: IBreezeEntity) {
        if (entity instanceof TeamLocation) {
            ArrayUtilities.addElementIfNotAlreadyExists(this.entitiesToConfirm, entity);
            ArrayUtilities.addElementIfNotAlreadyExists(this.teamLocations, entity);
            // Remove any locations that are not in the added state - that can only be detached
            this.teamLocations = this.teamLocations.filter((location) => location.entityAspect.entityState.isAdded());
        }
    }

    public cancel() {
        if (this.entitiesAreUnmodifiedOrInvalid && this.entitiesToConfirm.length > 0) {
            // this won't be prompted by base discard confirmation dialog and not discarded
            this.commonDataService.rejectChanges(this.entitiesToConfirm)
                .subscribe(() => super.cancel());
        } else {
            super.cancel();
        }
    }

    @Autobind
    public saveAndClose() {
        if (this.isOwnerTeam || this.isLeaderTeam) {
            // only leaders team builder on server will accept key function - for owner team, will create team location after create
            const createTeamAction = this.isOwnerTeam
                ? this.teamsService.createOwnerTeam(this.team.name, this.team.teamLeaderPerson.personId)
                : this.teamsService.createLeadersTeam(this.team.name, this.team.teamLeaderPerson.personId, this.popFirstKeyFunctionLocation()?.keyFunctionId);
            return createTeamAction.pipe(
                tap((team) => this.autoResolveData = team),
                switchMap((team) => this.attachTeamLocations(team!)),
                // entities created from the server - not needed anymore. discard before closing the dialog
                switchMap(() => this.commonDataService.rejectChanges(this.entitiesToConfirm)),
                tap(() => this.resolve(this.autoResolveData!)),
            );
        }

        // not changing team creation for non-owner/leaders teams
        return this.commonDataService.saveEntities(this.team).pipe(
            switchMap(() => {
                const teamLeaderConnection = this.team.teamLeaderPerson.getLatestConnection();
                if (teamLeaderConnection) {
                    return this.teamsService.promiseToUpdateRoleConnectionsForTeamLeaderChange(this.team, teamLeaderConnection);
                }
                return EMPTY;
            }),
            // cannot have roleConnections and teamLocations entities to be in a single save as teamLocation entities will need
            // to figure out if the person has the permission to save, which requires the role connections to do that.
            switchMap((roleConnections) => {
                const changedRoleConnections = roleConnections.filter((rc) => !!rc) as RoleConnection[];
                if (changedRoleConnections.length > 0) {
                    return this.commonDataService.saveEntities(changedRoleConnections);
                } else {
                    return of(undefined);
                }
            }),
            // this will save team locations if there is any
            switchMap(() => super.saveAndClose()),
            catchError((e) => {
                this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(e));
                return EMPTY;
            }),
        );
    }

    public onPrivateTeamChanged(isPrivate: boolean) {
        this.team.isPrivate = isPrivate;
        if (this.isPrivateNonStakeholderManagerOrCoach) {
            // only set teamLeaderPerson if the current user is not a stakeholder (if stakeholder, he can still access the team even if he is not in there)
            this.team.teamLeaderPersonId = this.userService.getCurrentPersonId();
        }
    }

    public onOwnerTeamChanged(isOwner: boolean) {
        this.isOwnerTeam = isOwner;
        if (isOwner) {
            this.team.isPrivate = true;
            this.isLeaderTeam = false;
            if (!this.team.name) {
                this.team.name = "Owner";
            }
        }
    }

    public onLeaderTeamChanged(isLeader: boolean) {
        this.isLeaderTeam = isLeader;
        if (isLeader) {
            this.isOwnerTeam = false;
            if (!this.team.name) {
                this.team.name = "Organisation leaders";
            }
        }
    }

    private popFirstKeyFunctionLocation() {
        const keyFunctionLocation = this.teamLocations.find((location) => location.entityAspect.entityState.isAdded() && location.keyFunctionId);
        if (keyFunctionLocation) {
            ArrayUtilities.removeElementFromArray(keyFunctionLocation, this.teamLocations);
        }

        return keyFunctionLocation;
    }

    private attachTeamLocations(team: Team) {
        const entitiesToSave: IBreezeEntity[] = [];
        if (this.team.parentTeamId) {
            team.parentTeamId = this.team.parentTeamId;
            entitiesToSave.push(team);
        }

        this.teamLocations = this.teamLocations.filter((location) => location.entityAspect.entityState.isAdded());
        for (const teamLocation of this.teamLocations) {
            teamLocation.team = team;
            entitiesToSave.push(teamLocation);
        }

        // no longer want to discard this as they will be changed to this newly created team and saved
        this.entitiesToConfirm = this.entitiesToConfirm.filter((entity) => !this.teamLocations.includes(entity as any));

        if (entitiesToSave.length > 0) {
            return this.commonDataService.saveEntities(entitiesToSave).pipe(
                map(() => team)
            );
        } else {
            return of(team);
        }
    }
}
